/**
 * @module AdaptersPage
 * @package @geenius/adapters/solidjs-ark
 * @description Ark-native SolidJS adapters overview page using Ark Dialog for
 * the configuration workflow.
 */

import { Dialog } from "@ark-ui/solid/dialog";
import type {
  AdapterDomain,
  AdapterStatusType,
  DomainAdapterConfig,
} from "@geenius/adapters";
import {
  ADAPTER_DOMAINS,
  ADAPTER_STATUSES,
  DOMAIN_LABELS,
  getDomainConfig,
  isAdaptersConfigured,
} from "@geenius/adapters";
import { createAdapters } from "@geenius/adapters/solidjs";
import type { JSX } from "solid-js";
import { createSignal, createUniqueId, For, Show } from "solid-js";
import { LoadingBridge } from "../bridges";
import { AdapterConfigForm } from "../components/AdapterConfigForm";
import { AdapterList } from "../components/AdapterList";
import { AdapterStatusBadge } from "../components/AdapterStatusBadge";
import { classNames } from "../lib/classNames";

type RegistryEntry = {
  domain: AdapterDomain;
  provider: string;
  status: AdapterStatusType;
};

/**
 * Props accepted by the Ark Solid adapters overview page.
 *
 * @property onNavigateDetail Optional callback used instead of opening the inline modal.
 * @property onConfigure Optional callback invoked with submitted adapter configuration.
 * @property class Optional class merged onto the page root.
 * @property configByDomain Optional configuration snapshot used to hydrate modal forms.
 * @property registryEntries Optional adapter registry snapshot rendered above the grid.
 */
export interface AdaptersPageProps {
  onNavigateDetail?: (domain: AdapterDomain) => void;
  onConfigure?: (
    domain: AdapterDomain,
    config: DomainAdapterConfig,
  ) => Promise<void> | void;
  class?: string;
  configByDomain?: Partial<Record<AdapterDomain, DomainAdapterConfig>>;
  registryEntries?: RegistryEntry[];
}

/** Renders the Ark Solid adapter management dashboard. */
export function AdaptersPage(props: AdaptersPageProps): JSX.Element {
  const ctx = createAdapters();
  const [selectedDomain, setSelectedDomain] =
    createSignal<AdapterDomain | null>(null);
  const [filterStatus, setFilterStatus] = createSignal<
    AdapterStatusType | undefined
  >();
  const modalTitleId = createUniqueId();
  const registryEntries = () => props.registryEntries ?? [];

  const connected = () =>
    ADAPTER_DOMAINS.filter(
      (domain) => ctx.statuses[domain]?.status === "connected",
    ).length;
  const errors = () =>
    ADAPTER_DOMAINS.filter((domain) => ctx.statuses[domain]?.status === "error")
      .length;
  const initialConfigFor = (domain: AdapterDomain) =>
    props.configByDomain?.[domain] ??
    (isAdaptersConfigured() ? getDomainConfig(domain) : undefined) ??
    undefined;

  return (
    <Show
      fallback={
        <div
          class={classNames("mx-auto max-w-4xl", props.class)}
          data-gn-adapters-ark-page="adapters"
        >
          <LoadingBridge class="min-h-72" label="Loading adapters" value={72} />
        </div>
      }
      when={!ctx.isLoading}
    >
      <div
        class={classNames(
          "mx-auto max-w-4xl space-y-8 text-slate-950",
          props.class,
        )}
        data-gn-adapters-ark-page="adapters"
      >
        <header class="space-y-1">
          <h1 class="text-2xl font-extrabold text-slate-950">Adapters</h1>
          <p class="text-sm text-slate-500">
            Manage your infrastructure adapters - database, auth, AI, storage,
            payments, and admin.
          </p>
        </header>

        <section class="grid gap-3 sm:grid-cols-3">
          <div class="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
            <p class="text-xs font-semibold uppercase text-slate-500">Total</p>
            <p class="mt-1 text-2xl font-extrabold text-slate-950">
              {ADAPTER_DOMAINS.length}
            </p>
            <p class="text-[0.625rem] text-slate-500">adapter domains</p>
          </div>
          <div class="rounded-md border border-emerald-500/30 bg-emerald-50 p-4 shadow-sm">
            <p class="text-xs font-semibold uppercase text-emerald-900">
              Connected
            </p>
            <p class="mt-1 text-2xl font-extrabold text-emerald-950">
              {connected()}
            </p>
            <p class="text-[0.625rem] text-emerald-900/70">active adapters</p>
          </div>
          <Show when={errors() > 0}>
            <div class="rounded-md border border-red-500/30 bg-red-50 p-4 shadow-sm">
              <p class="text-xs font-semibold uppercase text-red-900">Errors</p>
              <p class="mt-1 text-2xl font-extrabold text-red-950">
                {errors()}
              </p>
              <p class="text-[0.625rem] text-red-900/70">need attention</p>
            </div>
          </Show>
        </section>

        <Show when={registryEntries().length > 0}>
          <section class="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p class="text-xs font-semibold uppercase text-slate-500">
                  Adapter Registry
                </p>
                <p class="text-sm text-slate-500">
                  Live adapter registry entries.
                </p>
              </div>
              <div class="flex gap-3 text-xs text-slate-500">
                <span>{registryEntries().length} entries</span>
              </div>
            </div>
            <div class="mt-4 grid gap-2 sm:grid-cols-2">
              <For each={registryEntries()}>
                {(entry) => (
                  <div class="rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
                    <div class="flex items-center justify-between gap-3">
                      <div>
                        <p class="text-sm font-semibold text-slate-950">
                          {entry.domain}
                        </p>
                        <p class="text-xs text-slate-500">{entry.provider}</p>
                      </div>
                      <AdapterStatusBadge status={entry.status} />
                    </div>
                  </div>
                )}
              </For>
            </div>
          </section>
        </Show>

        <section class="flex flex-wrap items-center gap-2">
          <button
            aria-pressed={!filterStatus()}
            class={classNames(
              "rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors",
              !filterStatus()
                ? "border-slate-950 bg-slate-950 text-white"
                : "border-slate-200 bg-white text-slate-500 hover:text-slate-950",
            )}
            onClick={() => setFilterStatus(undefined)}
            type="button"
          >
            All
          </button>
          <For each={[...ADAPTER_STATUSES]}>
            {(status) => (
              <button
                aria-pressed={filterStatus() === status}
                class={classNames(
                  "flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors",
                  filterStatus() === status
                    ? "border-slate-950 bg-slate-950 text-white"
                    : "border-slate-200 bg-white text-slate-500 hover:text-slate-950",
                )}
                onClick={() => setFilterStatus(status)}
                type="button"
              >
                <AdapterStatusBadge status={status} showLabel={false} />
                {status}
              </button>
            )}
          </For>
        </section>

        <AdapterList
          filterStatus={filterStatus()}
          onSelect={(domain) => {
            if (props.onNavigateDetail) {
              props.onNavigateDetail(domain);
            } else {
              setSelectedDomain(domain);
            }
          }}
        />

        <Show when={selectedDomain()}>
          {(domain) => (
            <Dialog.Root
              modal
              onOpenChange={(details) => {
                if (!details.open) {
                  setSelectedDomain(null);
                }
              }}
              open
            >
              <Dialog.Backdrop class="fixed inset-0 z-50 bg-slate-950/40 backdrop-blur-sm" />
              <Dialog.Positioner class="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
                <Dialog.Content class="relative w-full max-w-lg outline-none">
                  <Dialog.Title class="sr-only" id={modalTitleId}>
                    Configure {DOMAIN_LABELS[domain()]}
                  </Dialog.Title>
                  <Dialog.CloseTrigger
                    aria-label="Close adapter configuration"
                    class="absolute right-3 top-3 z-10 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-500 shadow-sm hover:text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/40"
                  >
                    Close
                  </Dialog.CloseTrigger>
                  <AdapterConfigForm
                    domain={domain()}
                    initialConfig={initialConfigFor(domain())}
                    onCancel={() => setSelectedDomain(null)}
                    onSave={async (config) => {
                      await props.onConfigure?.(domain(), config);
                      setSelectedDomain(null);
                    }}
                    titleId={`${modalTitleId}-form`}
                  />
                </Dialog.Content>
              </Dialog.Positioner>
            </Dialog.Root>
          )}
        </Show>
      </div>
    </Show>
  );
}
